import { usePropertyInventory } from "../../../../../hooks/usePropertyInventory";
import {
  Card,
  CardContent,
  Typography,
  Box,
  alpha,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Home } from "@mui/icons-material";
import { Grid } from "@mui/system";
import { CreateApartmentForComplex } from "../../../../../types/propertyInventory/ApartmentComplex";
import { DocumentType } from "../../../../../types/propertyInventory/Tenant";

const documentTypeOptions = [
  { value: DocumentType.CEDULA_NACIONAL, label: "Cédula Nacional" },
  { value: DocumentType.DIMEX, label: "DIMEX" },
  { value: DocumentType.PASAPORTE, label: "Pasaporte" },
];

export const Step4Tenants = () => {
  const { apartmentForComplex, setApartmentForComplex } = usePropertyInventory();

  const isOccupied = apartmentForComplex?.apartmentStatus === 2;

  const handleTenantChange = (field: any, value: any) => {
    const updatedApartment = {
      ...apartmentForComplex,
      tenant: {
        ...apartmentForComplex?.tenant,
        [field]: value
      }
    };
    if(updatedApartment) {
      setApartmentForComplex(updatedApartment);
    }
  };

  const handleHistoryChange = (field: any, value: any) => {
    const updatedApartment = {
      ...apartmentForComplex,
      apartmentHistory: {
        ...apartmentForComplex?.apartmentHistory,
        [field]: value
      }
    };
    setApartmentForComplex(updatedApartment);
  };

  const isValidTenant = (apartment: CreateApartmentForComplex) => {
    const { tenant, apartmentHistory } = apartment;
    return (
      tenant.firstName?.trim() &&
      tenant.lastName?.trim() &&
      tenant.documentNumber?.trim() &&
      tenant.primaryPhone?.trim() &&
      apartmentHistory.moveInDate
    );
  };

  const isValid = isValidTenant(apartmentForComplex!);
  const { tenant, apartmentHistory } = apartmentForComplex || {};

  if (!isOccupied) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
            Información de Inquilinos
          </Typography>

          <Box sx={{
            p: 4,
            backgroundColor: alpha('#4CAF50', 0.1),
            borderRadius: 3,
            border: '2px solid #4CAF50',
            mb: 4
          }}>
            <Typography variant="h6" color="#388E3C" sx={{ mb: 2 }}>
              ¡No está ocupado!
            </Typography>
            <Typography color="text.secondary">
              El apartamento está marcado como disponible o no disponible.
            </Typography>
          </Box>

        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
          Información de Inquilino
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Completa los datos del inquilino para el apartamento ocupado.
        </Typography>

        <Grid container spacing={4}>
              <Grid size={{ xs: 12 }} key={apartmentForComplex.apartmentNumber}>
                <Card sx={{
                  p: 3,
                  backgroundColor: '#FAFAFA',
                  border: '2px solid',
                  borderColor: isValid ? '#4CAF50' : '#E0E0E0',
                  position: 'relative'
                }}>
                  {isValid && (
                    <Box sx={{
                      position: 'absolute',
                      top: 15,
                      right: 15,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Home sx={{ color: '#FF9800', mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                      Apartamento {apartmentForComplex.apartmentNumber}
                    </Typography>
                    <Chip
                      label="Ocupado"
                      size="small"
                      sx={{
                        ml: 2,
                        backgroundColor: alpha('#FF9800', 0.1),
                        color: '#F57C00'
                      }}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {/* Columna izquierda - Datos personales */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#2E7D7A' }}>
                        Datos Personales
                      </Typography>

                      <TextField
                        fullWidth
                        label="Nombre *"
                        value={tenant.firstName}
                        onChange={(e) => handleTenantChange('firstName', e.target.value)}
                        sx={{ mb: 2 }}
                        error={!tenant.firstName}
                        helperText={!tenant.firstName ? 'Campo requerido' : ''}
                      />

                      <TextField
                        fullWidth
                        label="Apellido *"
                        value={tenant.lastName}
                        onChange={(e) => handleTenantChange('lastName', e.target.value)}
                        sx={{ mb: 2 }}
                        error={!tenant.lastName}
                        helperText={!tenant.lastName ? 'Campo requerido' : ''}
                      />

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Tipo de Documento *</InputLabel>
                        <Select
                          value={tenant.documentType}
                          onChange={(e) => handleTenantChange('documentType', e.target.value)}
                          label="Tipo de Documento *"
                        >
                          {documentTypeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Número de Documento *"
                        value={tenant.documentNumber}
                        onChange={(e) => handleTenantChange('documentNumber', e.target.value)}
                        placeholder="1-2345-6789"
                        sx={{ mb: 2 }}
                        error={!tenant.documentNumber}
                        helperText={!tenant.documentNumber ? 'Campo requerido' : ''}
                      />

                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={tenant.email || ''}
                        onChange={(e) => handleTenantChange('email', e.target.value)}
                        placeholder="ejemplo@correo.com"
                        sx={{ mb: 2 }}
                      />

                    </Grid>

                    {/* Columna derecha - Datos adicionales */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#2E7D7A' }}>
                        Información Adicional
                      </Typography>

                      <TextField
                        fullWidth
                        label="Profesión"
                        value={tenant.profession || ''}
                        onChange={(e) => handleTenantChange('profession', e.target.value)}
                        placeholder="Ej: Ingeniero, Doctor, Estudiante"
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="Empleador"
                        value={tenant.employer || ''}
                        onChange={(e) => handleTenantChange('employer', e.target.value)}
                        placeholder="Nombre de la empresa"
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="Ingreso Mensual"
                        type="number"
                        value={tenant.monthlyIncome || ''}
                        onChange={(e) => handleTenantChange('monthlyIncome', parseFloat(e.target.value) || undefined)}
                        placeholder="Ingreso mensual aproximado"
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        fullWidth
                        label="Fecha de Ingreso *"
                        type="date"
                        value={apartmentHistory.moveInDate}
                        onChange={(e) => handleHistoryChange('moveInDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 2 }}
                        error={!apartmentHistory.moveInDate}
                        helperText={!apartmentHistory.moveInDate ? 'Campo requerido' : ''}
                      />

                      <TextField
                        fullWidth
                        label="Teléfono Principal *"
                        value={tenant.primaryPhone}
                        onChange={(e) => handleTenantChange('primaryPhone', e.target.value)}
                        placeholder="+506 8888-8888"
                        sx={{ mb: 2 }}
                        error={!tenant.primaryPhone}
                        helperText={!tenant.primaryPhone ? 'Campo requerido' : ''}
                      />
                      <TextField
                        fullWidth
                        label="Teléfono Secundario"
                        value={tenant.secondaryPhone || ''}
                        onChange={(e) => handleTenantChange('secondaryPhone', e.target.value)}
                        placeholder="+506 7777-7777"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};